import type { AxiosEventBus } from '@shared/axios/axiosEventBus';
import qs from 'qs';
import { useMemo } from 'react';

import createHttpClient from '@/api/client';
import { axiosEventBus } from '@/api/eventBus';
import { AiAdvisory } from '@/api/schema/AiAdvisory';
import { Comment } from '@/api/schema/Comment';
import { Currency } from '@/api/schema/Currency';
import { Estimation } from '@/api/schema/Estimation.ts';
import { EstimationCost } from '@/api/schema/EstimationCost';
import { EstimationItem } from '@/api/schema/EstimationItem';
import { EstimationItemAssumption } from '@/api/schema/EstimationItemAssumption';
import { EstimationItemCost } from '@/api/schema/EstimationItemCost';
import { EstimationItemResource } from '@/api/schema/EstimationItemResource';
import { EstimationModel } from '@/api/schema/EstimationModel.ts';
import { EstimationUserAssignment } from '@/api/schema/EstimationUserAssignment';
import { Localizations } from '@/api/schema/Localizations';
import { Methodology } from '@/api/schema/Methodology.ts';
import { Notification } from '@/api/schema/Notification.ts';
import { NotificationTemplates } from '@/api/schema/NotificationTemplates';
import { Permission } from '@/api/schema/Permission';
import { ProjectType } from '@/api/schema/ProjectType';
import { Tenant } from '@/api/schema/Tenant';
import { TenantNotificationConfiguration } from '@/api/schema/TenantNotificationConfiguration';
import { User } from '@/api/schema/User.ts';
import type { IApiContext } from '@/context/api/model';
import { useLocalization } from '@/context/localization';

import { useAuth } from '../auth';

const useHttpClient = (emit: AxiosEventBus['emit'], inferAcceptLanguage: boolean) => {
  const { auth } = useAuth();
  const {
    state: { locale },
  } = useLocalization();
  const httpClient = useMemo(
    () =>
      createHttpClient<string>(
        {
          ...(auth && {
            securityWorker: () => ({
              headers: {
                Authorization: `Bearer ${auth.accessToken}`,
                ...(inferAcceptLanguage ? { 'Accept-Language': locale } : {}),
              },
            }),
            paramsSerializer: (params: Record<string, unknown>) =>
              qs.stringify(params, { arrayFormat: 'repeat' }),
          }),
        },
        emit,
      ),
    [auth, locale, emit, inferAcceptLanguage],
  );

  return httpClient;
};

export const useApi = (): IApiContext => {
  const httpClient = useHttpClient(axiosEventBus.emit, true);
  const httpClientWithCustomCulture = useHttpClient(axiosEventBus.emit, false);

  return useMemo<IApiContext>(() => {
    return {
      aiAdvisoryController: new AiAdvisory(httpClient),
      projectTypesController: new ProjectType(httpClient),
      projectTypesWithCustomCultureController: new ProjectType(httpClientWithCustomCulture),
      methodologyController: new Methodology(httpClient),
      methodologyWithCustomCultureController: new Methodology(httpClientWithCustomCulture),
      estimationModelController: new EstimationModel(httpClient),
      estimationModelWithCustomCultureController: new EstimationModel(httpClientWithCustomCulture),
      estimationItemAssumptionController: new EstimationItemAssumption(httpClient),
      estimationItemController: new EstimationItem(httpClient),
      estimationItemResourceController: new EstimationItemResource(httpClient),
      estimationItemCostController: new EstimationItemCost(httpClient),
      estimationCostController: new EstimationCost(httpClient),
      notificationsController: new Notification(httpClient),
      notificationTemplatesController: new NotificationTemplates(httpClient),
      permissionController: new Permission(httpClient),
      userController: new User(httpClient),
      tenantController: new Tenant(httpClient),
      tenantNotificationConfigurationController: new TenantNotificationConfiguration(httpClient),
      estimationController: new Estimation(httpClient),
      estimationUserAssignmentController: new EstimationUserAssignment(httpClient),
      localizationsController: new Localizations(httpClient),
      currencyController: new Currency(httpClient),
      commentController: new Comment(httpClient),
    };
  }, [httpClient, httpClientWithCustomCulture]);
};
